// Merge hooks per-event + migrate stale relative-path entries + security prescan.
// Tách từ merge-pack-settings-into-project-settings.ts (A6 refactor) — GIỮ NGUYÊN
// hành vi. mergeHooksPerEvent + helper là INTERNAL (chỉ applyPackHooks gọi in-file);
// chỉ applyPackHooks export (barrel gọi để merge hooks, KHÔNG re-export → không public).

import type { PackSettingsTemplate } from "./merge-settings-types.js";
import { isCommandPathWithinWorkspace } from "./settings-command-path-safety.js";

// Normalize command để so sánh "logically same hook" giữa old relative-path
// và new ${CLAUDE_PROJECT_DIR}-path. Strip prefix để hai version cùng hook
// có chung normalized form.
//
// Examples:
//   "node .claude/hooks/foo.cjs"                        → "node .claude/hooks/foo.cjs"
//   "node ${CLAUDE_PROJECT_DIR}/.claude/hooks/foo.cjs"  → "node .claude/hooks/foo.cjs"
//   → cùng normalized form → coi là duplicate.
function normalizeCommandForMigration(command: string): string {
  return command.replace(/\$\{CLAUDE_PROJECT_DIR\}\//g, "").trim();
}

// Extract command string từ hook entry (best-effort — return all commands joined
// nếu entry có nhiều hooks; mỗi entry thường chỉ có 1 hook).
function extractCommandsFromEntry(entry: unknown): string[] {
  if (typeof entry !== "object" || entry === null) return [];
  const obj = entry as Record<string, unknown>;
  const hooks = Array.isArray(obj.hooks) ? obj.hooks : [];
  const cmds: string[] = [];
  for (const h of hooks) {
    if (typeof h === "object" && h !== null) {
      const cmd = (h as Record<string, unknown>).command;
      if (typeof cmd === "string") cmds.push(cmd);
    }
  }
  return cmds;
}

// Dedupe hook ở tầng HOOK (matcher + command), KHÔNG phải tầng group.
//
// BUG GỐC (v3.17.3): merge hook dùng unionDedupe — băm khoá bằng
// JSON.stringify(cả object group). Khi pack THÊM một hook vào group đã tồn tại,
// group pack (n+1 hook) có JSON khác group user (n hook) → coi là 2 thứ riêng
// biệt → append group mới, group cũ nằm lại vĩnh viễn. Hệ quả: n hook chung
// chạy 2 lần mỗi tool call.
//
// Tái hiện thật: telemetry tool.json v0.2.0 thêm planning-council-monitor-collector
// vào group PostToolUse có sẵn → settings.json thành 2 group (3 hook + 4 hook),
// 3 hook analytics/collector/askuserquestion chạy đúp mỗi Bash/Edit/Write.
//
// Khoá dedupe = matcher + command vì "cùng command, khác matcher" là 2 hook
// KHÁC nhau hợp lệ (vd pretooluse-collector chạy cho cả PreToolUse lẫn
// SubagentStart). First-occurrence-wins → thứ tự chạy hook giữ nguyên, đồng
// nhất với hành vi cũ của unionDedupe. Group rỗng sau khi lọc thì bỏ hẳn.
//
// LƯU Ý: dedupe theo command NGUYÊN VĂN, không normalize ${CLAUDE_PROJECT_DIR}.
// Việc dọn cặp old/new relative-path là trách nhiệm của
// migrateStaleRelativePathHooks chạy ngay sau — tách bạch 2 mối quan tâm.
function dedupeHookEntries(entries: unknown[]): unknown[] {
  const seen = new Set<string>();
  const out: unknown[] = [];

  for (const entry of entries) {
    if (typeof entry !== "object" || entry === null) {
      out.push(entry);
      continue;
    }
    const obj = entry as Record<string, unknown>;
    if (!Array.isArray(obj.hooks)) {
      // Entry không đúng shape { hooks: [...] } → giữ nguyên, không opinion.
      out.push(entry);
      continue;
    }

    const matcher = typeof obj.matcher === "string" ? obj.matcher : "";
    const keptHooks = obj.hooks.filter((h) => {
      if (typeof h !== "object" || h === null) return true;
      const command = (h as Record<string, unknown>).command;
      if (typeof command !== "string") return true;
      const key = `${matcher}::${command}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Mọi hook trong group đều là bản trùng → group rỗng, bỏ hẳn khỏi output.
    if (keptHooks.length === 0) continue;
    out.push(keptHooks.length === obj.hooks.length ? entry : { ...obj, hooks: keptHooks });
  }

  return out;
}

// v1.18.0 MIGRATION: Loại bỏ stale relative-path entries khi pack template/tool
// manifest đã chuyển sang ${CLAUDE_PROJECT_DIR}/. Background: v0.7.0 pack ship
// relative path "node .claude/hooks/foo.cjs"; Bash tool cd vào subdir làm cwd
// kẹt → hook fail ENOENT. v0.7.1 pack đổi sang "node ${CLAUDE_PROJECT_DIR}/...".
// Nhưng user settings.json đã có entry cũ — merge mới chỉ APPEND new, không xóa
// old → cả 2 entries cùng tồn tại, old fail noisy mỗi prompt.
//
// Migration logic:
// - Trong mỗi event, group entries theo normalized command.
// - Nếu group có cả old (no ${CLAUDE_PROJECT_DIR}) + new (with) → drop old, keep new.
// - Group chỉ có 1 version → giữ nguyên (không opinion về user's custom hooks).
//
// Returns: { migrated entries, count of dropped stale entries per event }
function migrateStaleRelativePathHooks(entries: unknown[]): {
  entries: unknown[];
  droppedCount: number;
} {
  // Group by normalized command form
  type IndexedEntry = { index: number; entry: unknown; commands: string[]; hasVar: boolean };
  const indexed: IndexedEntry[] = entries.map((entry, index) => {
    const commands = extractCommandsFromEntry(entry);
    const hasVar = commands.some((c) => c.includes("${CLAUDE_PROJECT_DIR}"));
    return { index, entry, commands, hasVar };
  });

  // Build normalized-command → list of indexed entries
  const byNormalized = new Map<string, IndexedEntry[]>();
  for (const ie of indexed) {
    const key = ie.commands.map(normalizeCommandForMigration).sort().join("|");
    if (!key) continue; // skip entries with no commands
    const list = byNormalized.get(key) ?? [];
    list.push(ie);
    byNormalized.set(key, list);
  }

  // Find indices to drop: groups with both old + new → drop old.
  const toDrop = new Set<number>();
  for (const [, items] of byNormalized) {
    if (items.length < 2) continue;
    const hasAnyNew = items.some((it) => it.hasVar);
    if (!hasAnyNew) continue;
    for (const it of items) {
      if (!it.hasVar) toDrop.add(it.index);
    }
  }

  if (toDrop.size === 0) {
    return { entries, droppedCount: 0 };
  }

  const filtered = entries.filter((_, i) => !toDrop.has(i));
  return { entries: filtered, droppedCount: toDrop.size };
}

// Merge hooks per-event: pack entries + user entries, dedupe theo (matcher + commands).
// Schema Claude Code: { EventName: [ { matcher?: string, hooks: [{type, command}] } ] }
//
// v1.18.0: Sau khi union dedupe, chạy migrateStaleRelativePathHooks để xóa entries
// relative-path cũ nếu đã có version ${CLAUDE_PROJECT_DIR} mới — fix cho upgrade
// từ pack pre-v0.7.1 lên v0.7.1+ (settings.json không tự cleanup khi tool.json
// đổi command string).
function mergeHooksPerEvent(
  packHooks: Record<string, unknown[]>,
  userHooks: Record<string, unknown[]>,
): { merged: Record<string, unknown[]>; touchedEvents: string[]; migratedCount: number } {
  const touched: string[] = [];
  const merged: Record<string, unknown[]> = { ...userHooks };
  let totalMigrated = 0;

  // First migrate existing user entries (handle case where user has stale entries
  // not from pack — eg. legacy install before sync).
  for (const event of Object.keys(merged)) {
    const userEntries = (merged[event] as unknown[]) || [];
    const { entries: migrated, droppedCount } = migrateStaleRelativePathHooks(userEntries);
    if (droppedCount > 0) {
      merged[event] = migrated;
      totalMigrated += droppedCount;
      if (!touched.includes(event)) touched.push(event);
    }
  }

  // Then union with pack entries + dedupe ở tầng hook.
  for (const [event, packEntries] of Object.entries(packHooks)) {
    const userEntries = (merged[event] as unknown[]) || [];
    const union = dedupeHookEntries([...userEntries, ...packEntries]);
    // Run migration again after union (in case pack new + user old now coexist).
    const { entries: postMigrate, droppedCount: postDropped } =
      migrateStaleRelativePathHooks(union);
    if (postDropped > 0) {
      totalMigrated += postDropped;
    }
    // So sánh SÂU chứ không so số lượng group: dedupe tầng hook có thể giữ
    // nguyên số group mà vẫn đổi nội dung bên trong (vd pack thêm 1 hook vào
    // group đã có → vẫn 1 group, nhưng nhiều hơn 1 hook). So theo length sẽ bỏ
    // sót → merged.hooks không được ghi → hook mới của pack im lặng biến mất.
    if (JSON.stringify(postMigrate) !== JSON.stringify(userEntries)) {
      if (!touched.includes(event)) touched.push(event);
    }
    merged[event] = postMigrate;
  }
  return { merged, touchedEvents: touched, migratedCount: totalMigrated };
}

// 6. hooks — merge per-event với dedupe + migrate stale relative-path entries.
// Lift nguyên văn step-6 của mergePackSettingsIntoProjectSettings (A6). Mutate
// merged.hooks + changes tại chỗ (behavior-preserving). Fail-closed: throw abort.
export function applyPackHooks(
  merged: PackSettingsTemplate,
  changes: string[],
  packTemplate: PackSettingsTemplate,
  userSettings: PackSettingsTemplate,
  workspacePath: string,
): void {
  if (packTemplate.hooks) {
    // SECURITY: reject hook command trỏ path RA NGOÀI workspace (absolute / `../`)
    // trước khi merge — chốt chặn supply chain nếu pack template (`.claude/pack/`,
    // populated qua `avatar sync`) bị compromise inject hook trỏ `/malicious/bin`
    // hoặc `../../evil`. Đối xứng với check statusLine (mục 1) + enableTool
    // (apply-tool-manifest-to-settings.ts). Fail-closed: throw abort TOÀN BỘ merge,
    // không skip riêng entry lỗi — pack compromise là supply-chain event, cần
    // dừng cứng + operator awareness.
    for (const [event, entries] of Object.entries(packTemplate.hooks)) {
      for (const entry of entries) {
        const hookList = (entry as { hooks?: Array<{ command?: string }> })?.hooks ?? [];
        for (const h of hookList) {
          if (!h.command) continue;
          const check = isCommandPathWithinWorkspace(workspacePath, h.command);
          if (!check.safe) {
            throw new Error(
              `Pack template hook (${event}) bị từ chối: ${check.reason}. ` +
                `Hook pack PHẢI dùng path tương đối tới workspace (vd \${CLAUDE_PROJECT_DIR}/.claude/...).`,
            );
          }
        }
      }
    }

    const userHooks = userSettings.hooks || {};
    const {
      merged: mergedHooks,
      touchedEvents,
      migratedCount,
    } = mergeHooksPerEvent(packTemplate.hooks, userHooks);
    if (touchedEvents.length > 0 || migratedCount > 0) {
      merged.hooks = mergedHooks;
      if (touchedEvents.length > 0) {
        changes.push(`hooks added for events: ${touchedEvents.join(", ")}`);
      }
      if (migratedCount > 0) {
        changes.push(
          `migrated ${migratedCount} stale relative-path hook entries to use \${CLAUDE_PROJECT_DIR} version (fixes hook failure when shell cwd changes)`,
        );
      }
    }
  }
}
